import React, { useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import ReportToolbar from './components/ReportToolbar';
import IepProgressReport from './components/reports/IepProgressReport';
import BehaviorTrendsReport from './components/reports/BehaviorTrendsReport';
import DataCollectionReport from './components/reports/DataCollectionReport';
import ComplianceReport from './components/reports/ComplianceReport';
import { computeDateRangePreset } from './reportUtils';

// ---------------------------------------------------------------------------
// Reports & Analytics
//
// Complete rewrite. Previously this page:
//   * simulated a 2-second "generating..." spinner over hardcoded data
//   * rendered fake "Alex Thompson / Maria Garcia" bar charts
//   * had a mock templates sidebar and a mock "AI insights" panel
//   * carried a mock CollaborationPanel with a comment feed
//
// It's now four real reports over the four contexts (students, goals,
// progress entries, incidents), a single date-range filter, a real
// multi-student picker, and a real CSV export button on each report.
//
// Everything renders synchronously from data already in memory (except
// the Data Collection report, which fetches goal_progress_entries in the
// selected window on demand via IEPGoalsContext.listAllProgressEntries).
// No mock generation delay, no mock insights, no mock collaborators.
// ---------------------------------------------------------------------------

const REPORT_TABS = [
  {
    id: 'iep-progress',
    label: 'IEP Progress',
    icon: 'Target',
    description: 'Where each student is against their IEP goals right now',
  },
  {
    id: 'behavior-trends',
    label: 'Behavior Trends',
    icon: 'AlertTriangle',
    description: 'Behavior incidents over time, by type, by intensity',
  },
  {
    id: 'data-collection',
    label: 'Data Collection',
    icon: 'Activity',
    description: 'How consistently your team is logging goal data',
  },
  {
    id: 'compliance',
    label: 'IEP Compliance',
    icon: 'Shield',
    description: 'Upcoming, due, and overdue annual reviews',
  },
];

const ReportsAndAnalytics = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeReport, setActiveReport] = useState('iep-progress');

  // Default: the current school year, since that's what a teacher wants to
  // see on Sept 1. The toolbar lets them override with presets or custom
  // date pickers.
  const [dateRange, setDateRange] = useState(() =>
    computeDateRangePreset('school-year'),
  );

  // Multi-select student filter. `null` / empty set means "all active
  // students" so a first-time visitor sees everyone.
  const [studentFilter, setStudentFilter] = useState(() => new Set());

  const activeTab = useMemo(
    () => REPORT_TABS.find((t) => t.id === activeReport) || REPORT_TABS[0],
    [activeReport],
  );

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <ReportToolbar
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          studentFilter={studentFilter}
          onStudentFilterChange={setStudentFilter}
        />

        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <Breadcrumb />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Reports
                </h1>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Print-ready summaries of your students' progress and
                  incidents. Pick a date range in the toolbar, then
                  choose a report below. Every report can be exported
                  to PDF or CSV for meetings.
                </p>
              </div>
            </div>

            {/* Report tab strip */}
            <div className="bg-surface border border-border rounded-lg overflow-hidden">
              <div className="flex overflow-x-auto">
                {REPORT_TABS.map((tab) => {
                  const isActive = tab.id === activeReport;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveReport(tab.id)}
                      className={`flex-1 min-w-[160px] flex flex-col items-start gap-1 px-4 py-3 text-left border-b-2 transition-colors ${
                        isActive
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:bg-muted/40'
                      }`}
                    >
                      <span className="flex items-center gap-2 font-medium text-sm text-foreground">
                        <Icon
                          name={tab.icon}
                          size={16}
                          className={
                            isActive ? 'text-primary' : 'text-muted-foreground'
                          }
                        />
                        {tab.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {tab.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Report content */}
            <div>
              {activeTab.id === 'iep-progress' && (
                <IepProgressReport
                  dateRange={dateRange}
                  studentFilter={studentFilter}
                />
              )}
              {activeTab.id === 'behavior-trends' && (
                <BehaviorTrendsReport
                  dateRange={dateRange}
                  studentFilter={studentFilter}
                />
              )}
              {activeTab.id === 'data-collection' && (
                <DataCollectionReport
                  dateRange={dateRange}
                  studentFilter={studentFilter}
                />
              )}
              {activeTab.id === 'compliance' && (
                <ComplianceReport
                  studentFilter={studentFilter}
                  dateRange={dateRange}
                />
              )}
            </div>

            {/* Provenance footer */}
            <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Icon
                  name="Info"
                  size={14}
                  className="mt-0.5 flex-shrink-0"
                />
                <p>
                  These reports read directly from your students,
                  behavior_incidents, behavior_goals, and
                  goal_progress_entries tables. No cached rollups, no
                  synthetic examples — what you see is what your
                  database currently contains. Every report has two
                  export buttons at the top: <strong>Print / Save as
                  PDF</strong> opens a print-optimized version with
                  every chart and table on one sheet, and{' '}
                  <strong>Export CSV</strong> downloads just the
                  underlying tabular data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportsAndAnalytics;
